import Communicator from 'models/communicator';
import createEnum from 'scripts/lib/create_enum';
import createModel, { prop } from './lib/create_model';
import TypeReflect from 'models/lib/type_reflect';
import keys from 'lodash/keys';
import { QuickReplyOption } from './chat_message_quick_reply_option';
import _ from 'lodash';
import InteractionType, { baseInteractionType } from 'models/interaction_type';
import ConversationItemType from 'models/conversation_item_type';
import { MessageChannelType } from 'models/conversation_message';

export const annotationDisplayType = createEnum('SINGLE_LINE');
export const dataDisplayType = createEnum('ORDER_STATUS', 'PRODUCT_LIST');
export const messageType = createEnum('ANNOTATION', 'DATA_DISPLAY', 'HTML', 'MENU', 'START', 'TEXT');
export const productInfoExtraAttributesSource = createEnum('VERTEX');

const StartContent = createModel({
  modelName: 'StartContent',

  properties: {
    automationId: prop(String),
    label: prop(String),
  },

  toString() {
    return this.label;
  },
});

const TextContent = createModel({
  modelName: 'TextContent',

  properties: {
    value: prop(String).isRequired,
  },

  toString() {
    return this.value;
  },
});

const MenuContent = createModel({
  modelName: 'MenuContent',

  properties: {
    prompt: prop(String).isRequired,
    menuItems: prop([QuickReplyOption]).default([]).isRequired,
  },

  toString() {
    return this.prompt;
  },
});

const FulfillmentContent = createModel({
  modelName: 'FulfillmentContent',

  properties: {
    estimatedDeliveryDate: prop(String),
    status: prop(String),
    trackingNumber: prop(String),
    trackingUrl: prop(String),
  },
});

const ProductContent = createModel({
  modelName: 'ProductContent',

  properties: {
    imageUrl: prop(String),
    name: prop(String),
    sku: prop(String),
    status: prop(String),
  },
});

const OrderStatusConfigContent = createModel({
  modelName: 'OrderStatusConfigContent',

  properties: {
    missingFulfillmentMessage: prop(String),
    missingStatusAndEtaMessage: prop(String),
    missingEtaMessage: prop(String),
  },
});

const DataDisplayOrderStatus = createModel({
  modelName: 'DataDisplayOrderStatus',

  properties: {
    config: prop(OrderStatusConfigContent),
    currencyCode: prop(String),
    customerOrderUrl: prop(String),
    fulfillments: prop([FulfillmentContent]).default([]),
    id: prop(String).isRequired,
    itemCount: prop(Number),
    products: prop([ProductContent]).default([]),
    totalAmount: prop(Number),
  },
});

const ProductInfoExtraAttributes = createModel({
  modelName: 'ProductInfoExtraAttributes',

  properties: {
    attributes: prop(Object).default({}),
    source: prop().oneOf(...keys(productInfoExtraAttributesSource)).isRequired,
  },
});

const ProductInfo = createModel({
  modelName: 'ProductInfo',

  properties: {
    description: prop(String),
    extraAttributes: prop(ProductInfoExtraAttributes),
    name: prop(String).isRequired,
    pageUrl: prop(String).isRequired,
    primaryImageUrl: prop(String),
    secondaryImageUrl: prop(String),
  },
});

const DataDisplayProductList = createModel({
  modelName: 'DataDisplayProductList',

  properties: {
    products: prop([ProductInfo]).default([]).isRequired,
  },
});

const DataDisplayTypeClasses = [
  [dataDisplayType.ORDER_STATUS, DataDisplayOrderStatus],
  [dataDisplayType.PRODUCT_LIST, DataDisplayProductList],
];
const dataDisplayTypeReflect = new TypeReflect(DataDisplayTypeClasses);

export const DataDisplayContent = createModel({
  modelName: 'DataDisplayContent',

  properties: {
    type: prop().oneOf(...keys(dataDisplayType)).isRequired,
    displayContent: prop().oneOf(DataDisplayOrderStatus, DataDisplayProductList).isRequired,
  },

  statics: {
    overrideFromJs() {
      return attrs => {
        let DataDisplayConstructor = dataDisplayTypeReflect.typeToConstructor(attrs.type);
        return new this({
          ...attrs,
          displayContent: DataDisplayConstructor.fromJs(attrs.displayContent),
        });
      };
    },
  },

  toString() {
    return this.type.toString();
  },
});

const SingleLineContent = createModel({
  modelName: 'SingleLineContent',

  properties: {
    icon: prop(String),
    description: prop(String),
  },
});

const AnnotationContent = createModel({
  modelName: 'AnnotationContent',

  properties: {
    type: prop().oneOf(..._.keys(annotationDisplayType)).isRequired,
    content: prop(SingleLineContent),
  },
});

const MessageTypeClasses = [
  [messageType.ANNOTATION, AnnotationContent],
  [messageType.DATA_DISPLAY, DataDisplayContent],
  [messageType.HTML, TextContent],
  [messageType.MENU, MenuContent],
  [messageType.START, StartContent],
  [messageType.TEXT, TextContent],
];
const messageTypeReflect = new TypeReflect(MessageTypeClasses);

export const AutomationSummary = createModel({
  modelName: 'AutomationSummary',

  properties: {
    description: prop(String).isRequired,
  },
});

const Initiator = createModel({
  modelName: 'Initiator',

  properties: {
    type: prop().oneOf(...keys(Communicator)).isRequired,
    id: prop(String).isRequired,
  },
});

const MessageAutomationSummaryContent = createModel({
  modelName: 'MessageAutomationSummaryContent',

  properties: {
    automationSessionID: prop(String).isRequired,
    companyAddress: prop(String).isRequired,
    customerAddress: prop(String).isRequired,
    interactionType: prop(String).isRequired,
    summary: prop(AutomationSummary).isRequired,
  },

  getMessageText() {
    return 'Message automation summary';
  },

  getChannelType() {
    const baseType = baseInteractionType(this.interactionType);

    switch (baseType) {
      case InteractionType.CHAT:
        return ConversationItemType.CHAT_MESSAGE;
      case InteractionType.CUSTOM_CHANNEL:
        return ConversationItemType.CUSTOM_CHANNEL_MESSAGE;
      case InteractionType.EMAIL:
        return ConversationItemType.EMAIL;
      case InteractionType.FB_MESSENGER:
        return ConversationItemType.FB_MESSAGE_OUTGOING;
      case InteractionType.INSTAGRAM_COMMENT:
        return MessageChannelType.INSTAGRAM_COMMENT;
      case InteractionType.INSTAGRAM_DIRECT:
        return MessageChannelType.INSTAGRAM_DIRECT;
      case InteractionType.PHONE_CALL:
        return ConversationItemType.PHONE_CALL;
      case InteractionType.SMS:
        return ConversationItemType.SMS;
      case InteractionType.TWITTER:
        return MessageChannelType.TWITTER;
      case InteractionType.WHATSAPP:
        return MessageChannelType.WHATSAPP;
      default:
        return this.interactionType;
    }
  },
});

export default MessageAutomationSummaryContent;

const AutomationMessage = createModel({
  modelName: 'AutomationMessage',

  properties: {
    id: prop(String).isRequired,
    from: prop(Initiator).isRequired,
    createdAt: prop(String).isRequired,
    type: prop().oneOf(...keys(messageType)).isRequired,
    content: prop().oneOf(MenuContent, StartContent, TextContent, DataDisplayContent, AnnotationContent).isRequired,
    context: prop(Object).default({}),
  },

  statics: {
    overrideFromJs() {
      return attrs => {
        let MessageConstructor = messageTypeReflect.typeToConstructor(attrs.type);
        return new this({
          ...attrs,
          from: Initiator.fromJs(attrs.from),
          content: MessageConstructor.fromJs(attrs.content),
        });
      };
    },
  },
});

export const MessageAutomationContent = createModel({
  // deprecated
  modelName: 'MessageAutomationContent',

  properties: {
    automationSessionID: prop(String).isRequired,
    companyAddress: prop(String).isRequired,
    customerAddress: prop(String).isRequired,
    interactionType: prop(String).isRequired,
    messages: prop([AutomationMessage]).default([]).isRequired,
    summary: prop(AutomationSummary).isRequired,
  },
});

export const MessageAutomationItemContent = createModel({
  modelName: 'MessageAutomationItemContent',

  properties: {
    message: prop(AutomationMessage).isRequired,
    interactionType: prop(String).isRequired,
  },

  from() {
    return _.capitalize(this.message.from.type);
  },
});
